import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ALL_EMAIL_TEMPLATES, ON_EMAIL_TEMPLATE_CHANGED } from '../../graphql/emailTemplates';
import Paginator from '../Paginator';
import { parsePaging, parseSorting } from '../../utils/queryString';
import Spinner from '../Spinner';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Error from '../errors/Error';
import EmailTemplatesList from './EmailTemplatesList';
import CreateEmailTemplateRow from './CreateEmailTemplateRow';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import SubscriptionActions from '../../utils/SubscriptionActions';
import { showInfoNotification } from '../../actions/notifications';
import Constants from '../../utils/Constants';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';

const TEMPLATE_SORT_KEYS = ['id', 'name', 'recipients', 'versionNumber'];

export function createNotificationText(t, action, name, id) {
    let notification;
    switch (action) {
        case SubscriptionActions.EMAIL_TEMPLATE_CREATED:
            notification = t('email-templates.notification.email-template-create-thirdparty', { name, id });
            break;
        case SubscriptionActions.EMAIL_TEMPLATE_DELETED:
            notification = t('email-templates.notification.email-template-delete-thirdparty', { name, id });
            break;
        default:
            notification = t('email-templates.notification.email-templates-modified-thirdparty');
    }
    return notification;
}

function EmailTemplates() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const parsedSorting = parseSorting(location.search);
    const sortKey = TEMPLATE_SORT_KEYS.includes(parsedSorting.sortKey) ? parsedSorting.sortKey : 'name';
    const sortDirection = parsedSorting.sortDirection || Constants.SORT_ASC;
    const dispatch = useDispatch();
    const [cloneSource, setCloneSource] = useState(null);
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_EMAIL_TEMPLATES, {
        variables: refetchProps,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    useExtendedSubscription(ON_EMAIL_TEMPLATE_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'emailTemplateChanged',
        refetchOnOwnResponse: true,
        onChangeCallback: ({ action, model: { name, id } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.EMAIL_TEMPLATE_EDIT])) {
                const notification = createNotificationText(t, action, name, id);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    if (loading) {
        result = <Spinner className="email-templates-loading" />;
    } else if (error) {
        result = <Error className="email-templates-error" error={error} />;
    } else {
        let activeProductType = data.allEmailTemplates.availableProductTypes?.length && data.allEmailTemplates.availableProductTypes[0];
        result = (
            <>
                <CreateEmailTemplateRow
                    availableProducts={data.allEmailTemplates.availableProducts}
                    cloneSource={cloneSource}
                    onCloneHandled={() => setCloneSource(null)}
                />
                <Paginator
                    totalCount={data.allEmailTemplates.emailTemplateList.totalCount}
                    perPage={perPage}
                    currentPage={refetchProps.page}
                    onPageChange={onPageChange}
                    bottom
                >
                    <EmailTemplatesList
                        availableProducts={data.allEmailTemplates.availableProducts}
                        activeProductType={activeProductType}
                        emailTemplates={data.allEmailTemplates.emailTemplateList.models}
                        onSort={onSort}
                        sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                        onClone={setCloneSource}
                    />
                </Paginator>
            </>
        );
    }
    return result;
}

export default EmailTemplates;
